import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { safeFormat } from '@/lib/safeDate';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Copy, CheckCircle2, XCircle, AlertTriangle, Lightbulb, Users, BarChart3 } from 'lucide-react';
import { useState } from 'react';

function ScoreBadge({ score, label }: { score: number | null; label?: string }) {
  if (score == null) return <span className="text-muted-foreground text-xs">—</span>;
  const color = score >= 70 ? 'text-green-600 bg-green-500/10' : score >= 40 ? 'text-amber-600 bg-amber-500/10' : 'text-red-600 bg-red-500/10';
  return (
    <div className="flex items-center justify-between gap-2">
      {label && <span className="text-xs text-muted-foreground truncate">{label}</span>}
      <span className={cn("text-xs font-bold px-2 py-0.5 rounded-full shrink-0", color)}>{score}</span>
    </div>
  );
}

function ScoreRow({ label, score }: { label: string; score: number | null }) {
  if (score == null) return null;
  const barColor = score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{score}/100</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", barColor)} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function BulletList({ items, icon, colorClass }: { items: string[] | null; icon: React.ReactNode; colorClass: string }) {
  if (!items || items.length === 0) return <span className="text-xs text-muted-foreground">Nenhum</span>;
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span className={cn("mt-0.5 shrink-0", colorClass)}>{icon}</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

interface QualityScoreDetailDialogProps {
  score: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QualityScoreDetailDialog({ score, open, onOpenChange }: QualityScoreDetailDialogProps) {
  const [copied, setCopied] = useState(false);

  if (!score) return null;

  const overallColor = (score.overall_score ?? 0) >= 70 ? 'text-green-600 bg-green-500/10 border-green-500/20' 
    : (score.overall_score ?? 0) >= 40 ? 'text-amber-600 bg-amber-500/10 border-amber-500/20' 
    : 'text-red-600 bg-red-500/10 border-red-500/20';

  const copyDealId = () => {
    navigator.clipboard.writeText(score.deal_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>Análise de Qualidade</span>
            <span className={cn("text-2xl font-bold px-3 py-1 rounded-lg border", overallColor)}>
              {score.overall_score ?? '—'}
            </span>
          </DialogTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <button onClick={copyDealId} className="font-mono hover:text-foreground transition-colors flex items-center gap-1">
              Deal: {score.deal_id}
              <Copy className="h-3 w-3" />
              {copied && <span className="text-green-600">✓</span>}
            </button>
            {score.analyzed_at && (
              <span>• {safeFormat(new Date(score.analyzed_at), 'dd/MM/yyyy HH:mm')}</span>
            )}
          </div>
        </DialogHeader>

        {/* SDR & Consultant Scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="space-y-3 p-4 rounded-lg border bg-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">SDR Performance</h3>
              <ScoreBadge score={score.sdr_overall_score} />
            </div>
            <ScoreRow label="First Contact Speed" score={score.sdr_first_contact_speed_score} />
            <ScoreRow label="Qualification" score={score.sdr_qualification_score} />
            <ScoreRow label="Meeting Setup" score={score.sdr_meeting_setup_score} />
            <ScoreRow label="Persistence" score={score.sdr_persistence_score} />
          </div>
          <div className="space-y-3 p-4 rounded-lg border bg-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Consultant Performance</h3>
              <ScoreBadge score={score.consultant_overall_score} />
            </div>
            <ScoreRow label="Discovery" score={score.consultant_discovery_score} />
            <ScoreRow label="Value Proposition" score={score.consultant_value_proposition_score} />
            <ScoreRow label="Objection Handling" score={score.consultant_objection_handling_score} />
            <ScoreRow label="Urgency Creation" score={score.consultant_urgency_creation_score} />
            <ScoreRow label="Next Steps" score={score.consultant_next_steps_score} />
            <ScoreRow label="Follow-up Quality" score={score.consultant_followup_quality_score} />
            <ScoreRow label="Personalization" score={score.consultant_personalization_score} />
          </div>
        </div>

        {/* Strengths / Weaknesses / Missed */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="space-y-2 p-4 rounded-lg border bg-card">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-green-600" /> Strengths
            </h3>
            <BulletList items={score.strengths} icon={<CheckCircle2 className="h-3 w-3" />} colorClass="text-green-600" />
          </div>
          <div className="space-y-2 p-4 rounded-lg border bg-card">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <XCircle className="h-4 w-4 text-red-600" /> Weaknesses
            </h3>
            <BulletList items={score.weaknesses} icon={<XCircle className="h-3 w-3" />} colorClass="text-red-600" />
          </div>
          <div className="space-y-2 p-4 rounded-lg border bg-card">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Missed Opportunities
            </h3>
            <BulletList items={score.missed_opportunities} icon={<AlertTriangle className="h-3 w-3" />} colorClass="text-amber-600" />
          </div>
        </div>

        {/* AI Recommendation */}
        {score.ai_recommendation && (
          <div className="mt-4 p-4 rounded-lg border border-primary/20 bg-primary/5">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <Lightbulb className="h-4 w-4 text-primary" /> AI Recommendation
            </h3>
            <p className="text-sm whitespace-pre-wrap">{score.ai_recommendation}</p>
          </div>
        )}

        {/* Team Effort Analysis */}
        {score.team_effort_analysis && (
          <div className="mt-4 p-4 rounded-lg border bg-card">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <Users className="h-4 w-4" /> Team Effort Analysis
            </h3>
            <p className="text-sm whitespace-pre-wrap">{score.team_effort_analysis}</p>
          </div>
        )}

        {/* Metrics footer */}
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground border-t pt-3">
          <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Messages: {score.messages_analyzed ?? 0}</span>
          <span>Calls: {score.calls_analyzed ?? 0}</span>
          <span>Tokens: {score.total_tokens_used?.toLocaleString() ?? 0}</span>
          <span>Model: {score.analysis_model ?? '—'}</span>
          <span>Version: {score.analysis_version ?? '—'}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TranscriptionDetailDialogProps {
  transcription: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TranscriptionDetailDialog({ transcription, open, onOpenChange }: TranscriptionDetailDialogProps) {
  const [copied, setCopied] = useState(false);

  if (!transcription) return null;

  const copyCallId = () => {
    navigator.clipboard.writeText(transcription.call_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transcrição Completa</DialogTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <button onClick={copyCallId} className="font-mono hover:text-foreground transition-colors flex items-center gap-1">
              Call: {transcription.call_id}
              <Copy className="h-3 w-3" />
              {copied && <span className="text-green-600">✓</span>}
            </button>
            {transcription.created_at && (
              <span>• {safeFormat(new Date(transcription.created_at), 'dd/MM/yyyy HH:mm')}</span>
            )}
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>Duração: {transcription.duration_seconds ?? '—'}s</span>
          <span>Confiança: {transcription.confidence_score != null ? `${(transcription.confidence_score * 100).toFixed(1)}%` : '—'}</span>
          <span>Custo: {transcription.cost_usd != null ? `$${transcription.cost_usd.toFixed(4)}` : '—'}</span>
          <span>Provider: {transcription.transcription_provider ?? '—'}</span>
          <span>Idioma: {transcription.transcription_language ?? '—'}</span>
        </div>

        <div className="mt-4 p-4 rounded-lg border bg-card max-h-[60vh] overflow-y-auto">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {transcription.transcription_text || 'Sem texto de transcrição.'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
